import { useCallback, useEffect, useState } from 'react';
import I18n from 'I18n';
import { gate } from 'hub-http/gates';
import { useIsUngated } from 'ai-components-ui-library/hooks/useIsUngated';
import { useSendOnCopilotChannel } from 'copilot-toolkit/channel/hooks/useSendOnCopilotChannel';
import { useIsUngatedForCopilotV2 } from 'ai-components-ui-library/hooks/useIsUngatedForCopilotV2';
import { useInlineFeedback } from 'ai-components-ui-library/components/feedback/hooks/useInlineFeedback';
import CopilotPreviewAutoGenerated from './internal/CopilotPreviewAutoGenerated';
import CopilotPreviewActionButton from './internal/CopilotPreviewActionButton';
import { useTracker } from '../../hooks/useTracker';
import { useShouldShowCopilotPreviews } from '../../hooks/useShouldShowCopilotPreviews';
import LEGACY_withErrorBoundary from '../error/LEGACY_withErrorBoundary';
import CopilotPreviewAutoGeneratedV2 from './internalV2/CopilotPreviewAutoGeneratedV2';
import { CopilotPreviewThroughActionButton } from './internalV2/CopilotPreviewThroughActionButton';
import { CopilotPreviewContextProvider } from './context/CopilotPreviewContextProvider';
import { jsx as _jsx } from "react/jsx-runtime";
const CopilotPreview = ({
  actionButtonRef,
  appName,
  summaryType,
  autoGenerate = false,
  children,
  copilotAction,
  copilotActionButtonText,
  hasAIBadge = true,
  numHoursTilSummaryStale = 24,
  numHoursTilSummaryStaleRefresh = 1,
  initialPromptMessage,
  onClick,
  onClose,
  onRefresh,
  onAskAQuestionClick,
  onQuickActionClick,
  onPreviewShown,
  previewTitle,
  previewKey,
  previewSource,
  showCloseButton = true,
  showAskAQuestionButton = true,
  usageTracker,
  usageTrackerAppLocationKey,
  useActionButton = true,
  actionButtonLeftHeader,
  overrideDisabled,
  onDontShowAgain
}) => {
  const isUngatedForCopilotV2 = useIsUngatedForCopilotV2();
  const isUngatedForCopilotPreviewsV2 = useIsUngated(gate('Copilot:PreviewsV2'));
  const {
    aiTracker
  } = useTracker(usageTracker, usageTrackerAppLocationKey);
  const launchCopilot = useSendOnCopilotChannel('LAUNCH_FROM_COPILOT_PREVIEW');
  const {
    onPositiveFeedback,
    onNegativeFeedback
  } = useInlineFeedback();

  // Improvements: replace callback/state management with magpie library
  const [dontShowAgainJustClicked, setDontShowAgainJustClicked] = useState(false);
  const {
    shouldShowCopilotPreviews
  } = useShouldShowCopilotPreviews({
    overrideDisabled: overrideDisabled === null || overrideDisabled === void 0 ? void 0 : overrideDisabled.isDisabled
  });
  const handleDontShowAgain = useCallback(() => {
    setDontShowAgainJustClicked(true);
    if (onDontShowAgain) {
      onDontShowAgain();
    }
  }, [onDontShowAgain]);
  const actionPayload = {
    data: {
      action: copilotAction,
      chatPreview: {
        previewSource,
        previewKey,
        // Default to true, and override if user interacts in some way.
        isAutoGenerated: true
      },
      sourceHostApp: appName,
      sourceHostAppPathname: window.location.pathname,
      source: 'ACTION'
    }
  };
  useEffect(() => {
    aiTracker.track('Ai Addon Copilot Preview', 'Preview Pageview', {
      action: 'page_view'
    });
  }, [aiTracker]);
  const handleCopilot = useCallback(activeResponse => {
    if (onAskAQuestionClick) {
      onAskAQuestionClick();
    }
    const followUpMessage = initialPromptMessage || I18n.text('aiAddonCopilotPreview.components.copilotPreview.chatWithCopilotButton.initialMessage');
    aiTracker.track('Ai Addon Copilot Preview', 'Copilot Preview Interaction', {
      action: 'ask_a_question',
      followUpMessage,
      isCopilotV2Enabled: isUngatedForCopilotV2 && isUngatedForCopilotPreviewsV2
    });
    if (activeResponse !== null && activeResponse !== void 0 && activeResponse.threadId) {
      launchCopilot({
        initialMessage: followUpMessage,
        threadId: activeResponse.threadId
      });
    } else if (!(activeResponse !== null && activeResponse !== void 0 && activeResponse.threadId)) {
      try {
        launchCopilot({
          threadId: undefined,
          initialMessage: followUpMessage,
          previewKey,
          previewSource: previewSource // Type assertion to handle potential type mismatch
        });
      } catch (error) {
        // If there's a type mismatch or other error, fall back to basic launch
        console.warn('Failed to launch copilot with preview data, falling back to basic launch:', error);
      }
    } else {
      launchCopilot(null);
    }
  }, [onAskAQuestionClick, initialPromptMessage, aiTracker, isUngatedForCopilotV2, isUngatedForCopilotPreviewsV2, launchCopilot, previewKey, previewSource]);
  if (!shouldShowCopilotPreviews || dontShowAgainJustClicked) {
    return null;
  }
  if (useActionButton) {
    if (isUngatedForCopilotV2 && isUngatedForCopilotPreviewsV2) {
      return /*#__PURE__*/_jsx(CopilotPreviewContextProvider, {
        children: /*#__PURE__*/_jsx(CopilotPreviewThroughActionButton, {
          actionButtonRef: actionButtonRef,
          actionPayload: actionPayload,
          aiTracker: aiTracker,
          showAskAQuestionButton: showAskAQuestionButton,
          summaryType: summaryType,
          autoGenerate: autoGenerate,
          copilotAction: copilotAction,
          copilotActionButtonText: copilotActionButtonText,
          hasAIBadge: hasAIBadge,
          numHoursTilSummaryStale: numHoursTilSummaryStale,
          numHoursTilSummaryStaleRefresh: numHoursTilSummaryStaleRefresh,
          onChatWithCopilot: handleCopilot,
          onClick: onClick,
          onClose: onClose,
          onNegativeFeedback: onNegativeFeedback,
          onPositiveFeedback: onPositiveFeedback,
          onPreviewShown: onPreviewShown,
          onQuickActionClick: onQuickActionClick,
          onRefresh: onRefresh,
          previewKey: previewKey,
          previewSource: previewSource,
          previewTitle: previewTitle,
          showCloseButton: showCloseButton,
          usageTrackerAppLocationKey: usageTrackerAppLocationKey,
          actionButtonLeftHeader: actionButtonLeftHeader,
          overrideDisabled: overrideDisabled,
          onDontShowAgain: handleDontShowAgain,
          children: children
        })
      });
    }
    return /*#__PURE__*/_jsx(CopilotPreviewActionButton, {
      actionButtonRef: actionButtonRef,
      actionPayload: actionPayload,
      aiTracker: aiTracker,
      autoGenerate: autoGenerate,
      copilotAction: copilotAction,
      copilotActionButtonText: copilotActionButtonText,
      hasAIBadge: hasAIBadge,
      numHoursTilSummaryStale: numHoursTilSummaryStale,
      numHoursTilSummaryStaleRefresh: numHoursTilSummaryStaleRefresh,
      onChatWithCopilot: handleCopilot,
      onClick: onClick,
      onClose: onClose,
      onNegativeFeedback: onNegativeFeedback,
      onPositiveFeedback: onPositiveFeedback,
      onPreviewShown: onPreviewShown,
      onQuickActionClick: onQuickActionClick,
      onRefresh: onRefresh,
      previewKey: previewKey,
      previewSource: previewSource,
      previewTitle: previewTitle,
      showCloseButton: showCloseButton,
      usageTrackerAppLocationKey: usageTrackerAppLocationKey,
      actionButtonLeftHeader: actionButtonLeftHeader,
      overrideDisabled: overrideDisabled,
      onDontShowAgain: handleDontShowAgain,
      children: children
    });
  } else {
    if (isUngatedForCopilotV2 && isUngatedForCopilotPreviewsV2) {
      return /*#__PURE__*/_jsx(CopilotPreviewContextProvider, {
        children: /*#__PURE__*/_jsx(CopilotPreviewAutoGeneratedV2, {
          summaryType: summaryType,
          showAskAQuestionButton: showAskAQuestionButton,
          aiTracker: aiTracker,
          copilotAction: copilotAction,
          hasAIBadge: hasAIBadge,
          numHoursTilSummaryStale: numHoursTilSummaryStale,
          onChatWithCopilot: handleCopilot,
          onNegativeFeedback: onNegativeFeedback,
          onPositiveFeedback: onPositiveFeedback,
          onQuickActionClick: onQuickActionClick,
          onRefresh: onRefresh,
          previewKey: previewKey,
          previewSource: previewSource,
          previewTitle: previewTitle,
          usageTrackerAppLocationKey: usageTrackerAppLocationKey,
          overrideDisabled: overrideDisabled,
          onDontShowAgain: handleDontShowAgain,
          children: children
        })
      });
    } else {
      return /*#__PURE__*/_jsx(CopilotPreviewAutoGenerated, {
        actionPayload: actionPayload,
        aiTracker: aiTracker,
        copilotAction: copilotAction,
        hasAIBadge: hasAIBadge,
        numHoursTilSummaryStale: numHoursTilSummaryStale,
        numHoursTilSummaryStaleRefresh: numHoursTilSummaryStaleRefresh,
        onChatWithCopilot: handleCopilot,
        onNegativeFeedback: onNegativeFeedback,
        onPositiveFeedback: onPositiveFeedback,
        onPreviewShown: onPreviewShown,
        onQuickActionClick: onQuickActionClick,
        onRefresh: onRefresh,
        previewKey: previewKey,
        previewSource: previewSource,
        previewTitle: previewTitle,
        usageTrackerAppLocationKey: usageTrackerAppLocationKey,
        overrideDisabled: overrideDisabled,
        onDontShowAgain: handleDontShowAgain,
        children: children
      });
    }
  }
};

/** We cannot safely migrate to the new error boundary because the types need to be updated as its not backwards compatible. */
export default LEGACY_withErrorBoundary(CopilotPreview);